import { BrowserRouter as Router, Routes, Route } from "react-router-dom"
import { useCallback, useEffect, useState } from "react"
import { supabase } from "./supabaseClient"
import { UserContext } from "./UserContext"
import { UserRoleContext } from "./UserRoleContext"
import { OrganizationContext } from "./OrganizationContext"
import ProtectedRoute from "./components/ProtectedRoute"
import NavBar from "./components/NavBar"
import BugReportWidget from "./components/BugReportWidget"
import { createRouteConfigs } from "./routes/config"
import {
  normalizeOrganizationType,
  organizationTheme,
} from "./utils/organizationProfile"
import { hasUnrestrictedProductAccess } from "./utils/organizationAccess"

const defaultPlan = {
  name: "free",
  ga4_enabled: true,
  website_audit_enabled: false,
  max_accounts: 1,
  monthly_scan_limit: 10,
  historical_retention_days: 0,
}

const defaultProductEntitlements = {
  ga4_audit: {
    slug: "ga4_audit",
    name: "GA4 Audit",
    status: "active",
    enabled: true,
    config: {},
  },
  asc_event_usage: {
    slug: "asc_event_usage",
    name: "ASC Event Usage",
    status: "active",
    enabled: false,
    config: {},
  },
  site_scraping: {
    slug: "site_scraping",
    name: "Site Scraping",
    status: "hidden",
    enabled: false,
    config: {},
  },
  paid_media: {
    slug: "paid_media",
    name: "Paid Media",
    status: "active",
    enabled: false,
    config: {},
  },
  conversion_monitoring: {
    slug: "conversion_monitoring",
    name: "Conversion Monitoring",
    status: "hidden",
    enabled: false,
    config: {},
  },
}

const normalizeProductEntitlements = (entitlements, plan = defaultPlan) => {
  const source = entitlements ?? {}
  const unrestricted = hasUnrestrictedProductAccess({ plan })
  const normalized = {
    ...defaultProductEntitlements,
    ...source,
  }

  return {
    ...normalized,
    ga4_audit: {
      ...normalized.ga4_audit,
      enabled:
        unrestricted ||
        (source.ga4_audit?.enabled ?? (plan?.ga4_enabled !== false)),
    },
    asc_event_usage: {
      ...normalized.asc_event_usage,
      enabled:
        unrestricted ||
        (source.asc_event_usage?.enabled ??
          (plan?.website_audit_enabled === true)),
    },
    site_scraping: {
      ...normalized.site_scraping,
      status: normalized.site_scraping?.status ?? "hidden",
      enabled: unrestricted || source.site_scraping?.enabled === true,
    },
    paid_media: {
      ...normalized.paid_media,
      enabled: unrestricted || source.paid_media?.enabled === true,
    },
    conversion_monitoring: {
      ...normalized.conversion_monitoring,
      enabled: unrestricted || source.conversion_monitoring?.enabled === true,
    },
  }
}

const productFlagsFromEntitlements = (
  entitlements,
  role = "free",
  plan = defaultPlan
) => {
  if (hasUnrestrictedProductAccess({ userRole: role, plan })) {
    return {
      ga4AuditEnabled: true,
      ascEventUsageEnabled: true,
      siteScrapingEnabled: true,
      paidMediaEnabled: true,
      conversionMonitoringEnabled: true,
    }
  }
  return {
    ga4AuditEnabled: entitlements.ga4_audit?.enabled !== false,
    ascEventUsageEnabled: entitlements.asc_event_usage?.enabled === true,
    siteScrapingEnabled: entitlements.site_scraping?.enabled === true,
    paidMediaEnabled: entitlements.paid_media?.enabled === true,
    conversionMonitoringEnabled: entitlements.conversion_monitoring?.enabled === true,
  }
}

const planWithEntitlements = (plan, entitlements) => ({
  ...defaultPlan,
  ...(plan ?? {}),
  ...productFlagsFromEntitlements(entitlements),
  ga4_enabled: entitlements.ga4_audit?.enabled !== false,
  website_audit_enabled: entitlements.asc_event_usage?.enabled === true,
  paid_media_enabled: entitlements.paid_media?.enabled === true,
  conversion_monitoring_enabled: entitlements.conversion_monitoring?.enabled === true,
})

export const isMetaOAuthCallback = (url) =>
  url.pathname === "/paid-media" && url.searchParams.has("state")

export const isMonitoringOAuthCallback = (url) =>
  url.pathname === "/clients" &&
  ["monitoring_google", "measurement_documentation"].includes(
    url.searchParams.get("oauth_provider")
  ) &&
  url.searchParams.has("state")

function App() {
  const [user, setUser] = useState(null)
  const [role, setRole] = useState("free")
  const [authChecked, setAuthChecked] = useState(false)
  const [organizations, setOrganizations] = useState([])
  const [selectedOrgId, setSelectedOrgIdState] = useState(() =>
    localStorage.getItem("selectedOrgId")
  )

  const setSelectedOrgId = (id) => {
    setSelectedOrgIdState(id)
    if (id) localStorage.setItem("selectedOrgId", id)
    else localStorage.removeItem("selectedOrgId")
  }

  const loadRole = useCallback(async (uid) => {
    try {
      // Fetch existing role without overriding it
      let { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", uid)
        .maybeSingle()

      if (!error && data) {
        setRole(data.role)
        return
      }

      const { data: inserted, error: insertError } = await supabase
        .from("user_roles")
        .insert({ user_id: uid })
        .select("role")
        .single()

      if (!insertError && inserted) {
        setRole(inserted.role)
      }
    } catch (err) {
      console.error("loadRole error", err)
      setRole("free")
    }
  }, [])

  const loadOrganizations = useCallback(async (uid) => {
    try {
      console.log("loadOrganizations via RPC", uid)
      const { error: acceptError } = await supabase.rpc(
        "accept_pending_invites_for_user"
      )
      if (acceptError) {
        console.error("accept_pending_invites_for_user error", acceptError)
      }
      const { data, error } = await supabase.rpc("get_user_organizations")
      console.log("get_user_organizations result", { data, error })
      if (error) {
        console.error("get_user_organizations error", error)
        setOrganizations([])
        setSelectedOrgId(null)
        return
      }

      let orgs = (data ?? []).map((o) => {
        const productEntitlements = normalizeProductEntitlements(
          o.product_entitlements,
          o.plan ?? defaultPlan
        )
        return {
          id: o.id,
          name: o.name,
          organizationType: normalizeOrganizationType(o.organization_type),
          customAuditRulesEnabled: o.custom_audit_rules_enabled === true,
          plan: planWithEntitlements(o.plan, productEntitlements),
          productEntitlements,
        }
      })
      console.log("Final organizations with product entitlements", orgs)
      setOrganizations(orgs)
      const storedOrgId = localStorage.getItem("selectedOrgId")
      if (orgs.length === 0) setSelectedOrgId(null)
      else if (storedOrgId && orgs.some((o) => o.id === storedOrgId))
        setSelectedOrgId(storedOrgId)
      else if (!selectedOrgId || !orgs.some((o) => o.id === selectedOrgId))
        setSelectedOrgId(orgs[0].id)
    } catch (err) {
      console.error("loadOrganizations RPC error", err)
      setOrganizations([])
      setSelectedOrgId(null)
    }
  }, [selectedOrgId])

  useEffect(() => {
    const initAuth = async () => {
      const url = new URL(window.location.href)
      const code = url.searchParams.get("code")
      if (code && !isMetaOAuthCallback(url) && !isMonitoringOAuthCallback(url)) {
        const { error } = await supabase.auth.exchangeCodeForSession(code)
        if (!error) {
          window.history.replaceState({}, document.title, url.pathname)
        }
      }

      let {
        data: { session },
      } = await supabase.auth.getSession()
      if (!session || (session.expires_at && session.expires_at * 1000 < Date.now())) {
        const { data: refreshed, error } = await supabase.auth.refreshSession()
        if (!error && refreshed?.session) {
          session = refreshed.session
        }
      }
      const currentUser = session?.user ?? null
      setUser(currentUser)
      if (currentUser) {
        // Keep protected routes in their loading state until organization data
        // is ready. Otherwise a fresh OAuth callback can be redirected away
        // from /paid-media before that page processes Meta's code and state.
        await Promise.all([
          loadRole(currentUser.id),
          loadOrganizations(currentUser.id),
        ])
      }
      setAuthChecked(true)

      if (window?.name === "ga4oauth" && window.opener) {
        try {
          window.opener.postMessage(
            { type: "ga4-oauth-complete" },
            window.location.origin
          )
        } catch (err) {
          console.warn("Unable to notify opener about GA4 OAuth completion", err)
        }
        try {
          window.close()
        } catch (err) {
          console.warn("Unable to close GA4 OAuth popup window", err)
        }
      }
    }

    initAuth()

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        const authUser = session?.user ?? null
        setUser(authUser)
        if (authUser) {
          loadRole(authUser.id)
          loadOrganizations(authUser.id)
        } else setRole("free")
      }
    )

    return () => listener?.subscription?.unsubscribe()
  }, [loadOrganizations, loadRole])

  if (!authChecked) return <div>Loading...</div>

  const needsOrg = !!user && organizations.length === 0
  const selectedOrg = organizations.find((o) => o.id === selectedOrgId)
  const theme = organizationTheme(selectedOrg)
  console.log("Selected organization", selectedOrg)
  const productEntitlements = selectedOrg?.productEntitlements ?? defaultProductEntitlements
  const {
    ga4AuditEnabled,
    ascEventUsageEnabled,
    siteScrapingEnabled,
    paidMediaEnabled,
    conversionMonitoringEnabled,
  } = productFlagsFromEntitlements(productEntitlements, role, selectedOrg?.plan)
  const automotiveAscEventUsageEnabled =
    theme.isAutomotive && ascEventUsageEnabled
  console.log("Feature flags", {
    ga4AuditEnabled,
    ascEventUsageEnabled: automotiveAscEventUsageEnabled,
    automotiveFeaturesEnabled: theme.isAutomotive,
    siteScrapingEnabled,
    paidMediaEnabled,
    conversionMonitoringEnabled,
    productEntitlements,
  })

  const routeConfigs = createRouteConfigs({
    needsOrg,
    selectedOrgId,
    ga4AuditEnabled,
    ascEventUsageEnabled: automotiveAscEventUsageEnabled,
    automotiveFeaturesEnabled: theme.isAutomotive,
    siteScrapingEnabled,
    paidMediaEnabled,
    conversionMonitoringEnabled,
  })

  return (
    <UserContext.Provider value={user}>
      <UserRoleContext.Provider value={role}>
        <OrganizationContext.Provider
          value={{
            organizations,
            selectedOrgId,
            selectedOrganization: selectedOrg ?? null,
            ...theme,
            ascEventUsageEnabled: automotiveAscEventUsageEnabled,
            setSelectedOrgId,
            refreshOrganizations: () => {
              if (user) loadOrganizations(user.id)
            },
          }}>
          <Router>
            <div
              className={`app-shell app-shell--${theme.organizationType}`}
              data-organization-type={theme.organizationType}
            >
              {user && !needsOrg && <NavBar />}
              {user && <BugReportWidget />}
              <Routes>
                {routeConfigs.map(({ path, component, render, guard }) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <ProtectedRoute
                        component={component}
                        render={render}
                        {...guard}
                      />
                    }
                  />
                ))}
              </Routes>
            </div>
          </Router>
        </OrganizationContext.Provider>
      </UserRoleContext.Provider>
    </UserContext.Provider>
  )
}

export default App
